import React, { useState, useEffect, useRef } from 'react';
import hotelService from '../services/hotelService';
import MainLayout from '../components/Layout/MainLayout';
import {
  HotelCatalog,
  SearchForm,
  HotelSearchResults
} from '../components/hotelList';

const HotelSearchPage = () => {
  const [searchForm, setSearchForm] = useState({
    latitude: '',
    longitude: '',
    checkInDate: '',
    checkOutDate: '',
    adults: 1, // Changed from 2 to 1 for better availability
    children: 0,
    childAges: [], // Array to store ages of children
    currency: 'USD',
    radius: 200, // Maximum allowed by Sabre API
    page: 1,
    itemsPerPage: 20,
    category: 'all'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [displayLocationQuery, setDisplayLocationQuery] = useState(''); // For display in results header
  const [hotelSuggestions, setHotelSuggestions] = useState([]);
  const [showHotelSuggestions, setShowHotelSuggestions] = useState(false);
  const [hotelQuery, setHotelQuery] = useState('');
  const [displayHotelQuery, setDisplayHotelQuery] = useState(''); // For display in results header
  const [hotels, setHotels] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalItems: 0,
    hasMorePages: false
  });
  const [catalogHotels, setCatalogHotels] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // New states for tracking search type and selected hotel
  const [searchType, setSearchType] = useState('location'); // 'location' or 'hotel'
  const [selectedHotelCode, setSelectedHotelCode] = useState('');
  const [selectedLocationType, setSelectedLocationType] = useState(''); // Track the type of location selected

  const [dropdownsDisabled, setDropdownsDisabled] = useState(false);
  const dropdownsDisabledRef = useRef(false);

  // Set default dates
  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    setSearchForm(prev => ({
      ...prev,
      checkInDate: tomorrow.toISOString().split('T')[0],
      checkOutDate: nextWeek.toISOString().split('T')[0]
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      setCatalogLoading(true);
      try {
        const data = await hotelService.getCatalogHotels(18);
        if (!cancelled) {
          setCatalogHotels(data?.hotels || []);
        }
      } catch (catalogError) {
        console.error('Error loading catalog hotels:', catalogError);
        if (!cancelled) setCatalogHotels([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside search form elements
      const searchFormElement = document.querySelector('#search');
      const dropdownElement = document.querySelector('[data-dropdown="suggestions"]');

      if (searchFormElement && !searchFormElement.contains(event.target) &&
        (!dropdownElement || !dropdownElement.contains(event.target))) {
        setShowSuggestions(false);
        setShowHotelSuggestions(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user's current location
  const getCurrentLocation = () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSearchForm(prev => ({
            ...prev,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          }));
          setLocationQuery(`${position.coords.latitude}, ${position.coords.longitude}`);
          setLoading(false);
        },
        (error) => {
          setError('Unable to get your location. Please enter coordinates manually.');
          setLoading(false);
        }
      );
    } else {
      setError('Geolocation is not supported by this browser.');
      setLoading(false);
    }
  };

  // Search for location suggestions with enhanced categories
  const searchLocations = async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const data = await hotelService.getGeoAutocomplete(query, {
        categories: ['CITY', 'AIR', 'POI'], // Include cities, airports, and points of interest
        limit: 15 // Increased limit to accommodate multiple categories
      });

      setLocationSuggestions(data.Results || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      setLocationSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Search for hotel name suggestions
  const searchHotels = async (query) => {
    if (!query || query.length < 3) {
      setHotelSuggestions([]);
      setShowHotelSuggestions(false);
      return;
    }

    try {
      const data = await hotelService.getHotelNameAutocomplete(query);

      // Fix: The backend returns data.response.docs, not data.Results
      const hotels = data?.response?.docs || [];

      setHotelSuggestions(hotels);
      setShowHotelSuggestions(true);
    } catch (error) {
      console.error('Error fetching hotel suggestions:', error);
      setHotelSuggestions([]);
      setShowHotelSuggestions(false);
    }
  };

  // Handle hotel name input change
  const handleHotelNameChange = (e) => {
    const value = e.target.value;
    setHotelQuery(value);

    // When user starts typing hotel name, prepare for hotel search mode but don't set it yet
    // Search type will be set when they actually select a hotel from suggestions
    if (value.length === 0) {
      // If they clear the hotel field, reset to location mode
      setSearchType('location');
      setSelectedHotelCode('');
    }

    // Debounce search
    clearTimeout(window.hotelSearchTimeout);
    window.hotelSearchTimeout = setTimeout(() => {
      searchHotels(value);
    }, 300);
  };

  // Select a hotel from suggestions
  const selectHotel = (hotel) => {
    // Fix: Backend returns hotelName, not name
    const hotelName = hotel.hotelName || hotel.name || 'Unknown Hotel';
    const hotelCode = hotel.hotelCode || hotel.globalID || hotel.id;


    // Immediately close dropdown and clear suggestions
    setShowHotelSuggestions(false);
    setShowSuggestions(false);
    setHotelSuggestions([]);
    setLocationSuggestions([]);

    // Update the search field with hotel name
    setHotelQuery(hotelName);

    // Clear location query to show hotel name in search field
    setLocationQuery('');

    // Set search type to hotel and store hotel code
    setSearchType('hotel');
    setSelectedHotelCode(hotelCode);
    setSelectedLocationType(''); // Clear location type when selecting hotel

    // Store hotel data in search form for potential fallback
    if (hotel.latitude && hotel.longitude) {
      setSearchForm(prev => ({
        ...prev,
        latitude: hotel.latitude.toString(),
        longitude: hotel.longitude.toString(),
        page: 1 // Reset to page 1 when hotel changes
      }));
    } else {
      // Clear coordinates if hotel doesn't have location data
      setSearchForm(prev => ({
        ...prev,
        latitude: '',
        longitude: '',
        page: 1 // Reset to page 1 when hotel changes
      }));
    }

    // Also reset pagination state
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Handle location input change
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);

    // When user starts typing location, reset to location search mode
    setSearchType('location');
    setSelectedHotelCode('');
    setSelectedLocationType(''); // Clear location type when typing new location
    setHotelQuery('');

    // Clear coordinates when typing new location
    setSearchForm(prev => ({
      ...prev,
      latitude: '',
      longitude: ''
    }));

    // Debounce search
    clearTimeout(window.locationSearchTimeout);
    window.locationSearchTimeout = setTimeout(() => {
      searchLocations(value);
    }, 300);
  };

  // Select a location from suggestions
  const selectLocation = (location) => {
    const cityName = safeString(location.city, 'Unknown City');
    const countryName = safeString(location.countryName, location.country, 'Unknown Country');

    // Create appropriate display name based on location type
    let displayLocationName = '';

    if (location.isAirport) {
      // For airports, show airport name with city, country
      const airportName = safeString(location.name || location.displayName, 'Airport');
      displayLocationName = `${airportName}, ${cityName}, ${countryName}`;
    } else if (location.isPOI) {
      // For POIs, show POI name with city, country
      const poiName = safeString(location.name || location.displayName, 'Point of Interest');
      displayLocationName = `${poiName}, ${cityName}, ${countryName}`;
    } else if (location.isRailway) {
      // For railway stations, show station name with city, country
      const stationName = safeString(location.name || location.displayName, 'Railway Station');
      displayLocationName = `${stationName}, ${cityName}, ${countryName}`;
    } else if (location.isCity) {
      // For cities, show city, state/province, country
      const stateName = safeString(location.stateName);
      if (stateName) {
        displayLocationName = `${cityName}, ${stateName}, ${countryName}`;
      } else {
        displayLocationName = `${cityName}, ${countryName}`;
      }
    } else {
      // Fallback to original logic
      displayLocationName = `${cityName}, ${countryName}`;
    }

    // Immediately close dropdown and clear suggestions
    setShowSuggestions(false);
    setShowHotelSuggestions(false);
    setLocationSuggestions([]);
    setHotelSuggestions([]);

    // Clear hotel query and set location query with enhanced display
    setHotelQuery('');
    setLocationQuery(displayLocationName);

    // Set search type to location and clear hotel code
    setSearchType('location');
    setSelectedHotelCode('');

    // Track the selected location type for enhanced display
    if (location.isAirport) {
      setSelectedLocationType('airport');
    } else if (location.isPOI) {
      setSelectedLocationType('poi');
    } else if (location.isRailway) {
      setSelectedLocationType('railway');
    } else if (location.isCity) {
      setSelectedLocationType('city');
    } else {
      setSelectedLocationType('location');
    }

    setSearchForm(prev => ({
      ...prev,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      page: 1 // Reset to page 1 when location changes
    }));

    // Also reset pagination state
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'children') {
      const numChildren = parseInt(value) || 0;
      setSearchForm(prev => ({
        ...prev,
        [name]: value,
        childAges: Array(numChildren).fill(10) // Automatically set all children ages to 10
      }));
    } else {
      setSearchForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    if (!hotelQuery && (!searchForm.latitude || !searchForm.longitude)) {
      setError('Please select a location or search for a specific hotel');
      return false;
    }

    if (!searchForm.checkInDate || !searchForm.checkOutDate) {
      setError('Please select check-in and check-out dates');
      return false;
    }

    const checkIn = new Date(searchForm.checkInDate);
    const checkOut = new Date(searchForm.checkOutDate);
    const today = new Date();

    if (checkIn < today) {
      setError('Check-in date cannot be in the past');
      return false;
    }

    if (checkOut <= checkIn) {
      setError('Check-out date must be after check-in date');
      return false;
    }

    return true;
  };

  // Search for hotels
  const searchForHotels = async (pageOverride = null, categoryOverride = null) => {
    const category = categoryOverride || searchForm.category || 'all';
    setLoading(true);
    setError('');
    setHotels([]); // Clear previous results immediately

    // Close dropdowns when search starts
    setShowSuggestions(false);
    setShowHotelSuggestions(false);

    // Update display values immediately when search starts
    if (searchType === 'hotel' && selectedHotelCode) {
      setDisplayHotelQuery(hotelQuery);
      setDisplayLocationQuery(''); // Clear location display for hotel search
    } else {
      setDisplayLocationQuery(locationQuery);
      setDisplayHotelQuery(''); // Clear hotel display for location search
    }

    try {
      // Test API connection first
      await hotelService.testConnection();

      let data;
      let searchData; // Declare searchData at function scope

      if (searchType === 'hotel' && selectedHotelCode) {
        // Search for specific hotel by hotel code

        const hotelSearchData = {
          hotelCode: selectedHotelCode,
          checkInDate: searchForm.checkInDate,
          checkOutDate: searchForm.checkOutDate,
          adults: parseInt(searchForm.adults),
          children: parseInt(searchForm.children),
          childAges: searchForm.childAges,
          currency: searchForm.currency || 'USD'
        };

        data = await hotelService.searchHotelsByHotelCode(hotelSearchData);


        // For hotel search, create a simplified searchData for pagination
        searchData = {
          page: 1,
          itemsPerPage: 20,
          adults: parseInt(searchForm.adults),
          children: parseInt(searchForm.children),
          childAges: searchForm.childAges
        };
      } else {
        // Search by location coordinates or find best match if no coordinates
        if (searchForm.latitude && searchForm.longitude) {
          // User selected from dropdown - use exact coordinates

          searchData = {
            ...searchForm,
            latitude: parseFloat(searchForm.latitude),
            longitude: parseFloat(searchForm.longitude),
            adults: parseInt(searchForm.adults),
            children: parseInt(searchForm.children),
            childAges: searchForm.childAges,
            radius: parseInt(searchForm.radius),
            page: pageOverride !== null ? parseInt(pageOverride) : parseInt(searchForm.page),
            itemsPerPage: parseInt(searchForm.itemsPerPage),
            category
          };

          data = await hotelService.searchHotelsByCoordinates(searchData);
        } else if (locationQuery && locationQuery.trim() !== '') {
          // User typed but didn't select from dropdown - find best match using autocomplete

          try {
            // Call autocomplete API to find best match with enhanced categories
            const autocompleteData = await hotelService.getGeoAutocomplete(locationQuery, {
              categories: ['CITY', 'AIR', 'POI'],
              limit: 5
            });
            const locations = autocompleteData?.Results || [];

            if (locations.length > 0) {
              // Use the first (best) match
              const bestMatch = locations[0];

              // Create enhanced display name using the same logic as selectLocation
              let displayName = '';
              const cityName = safeString(bestMatch.city, 'Unknown City');
              const countryName = safeString(bestMatch.countryName, bestMatch.country, 'Unknown Country');

              if (bestMatch.isAirport) {
                const airportName = safeString(bestMatch.name || bestMatch.displayName, 'Airport');
                displayName = `${airportName}, ${cityName}, ${countryName}`;
              } else if (bestMatch.isPOI) {
                const poiName = safeString(bestMatch.name || bestMatch.displayName, 'Point of Interest');
                displayName = `${poiName}, ${cityName}, ${countryName}`;
              } else if (bestMatch.isRailway) {
                const stationName = safeString(bestMatch.name || bestMatch.displayName, 'Railway Station');
                displayName = `${stationName}, ${cityName}, ${countryName}`;
              } else if (bestMatch.isCity) {
                const stateName = safeString(bestMatch.stateName);
                if (stateName) {
                  displayName = `${cityName}, ${stateName}, ${countryName}`;
                } else {
                  displayName = `${cityName}, ${countryName}`;
                }
              } else {
                // Fallback to original logic
                displayName = bestMatch.city && bestMatch.countryName ?
                  `${bestMatch.city}, ${bestMatch.countryName}` :
                  (bestMatch.city || bestMatch.countryName || locationQuery);
              }

              setDisplayLocationQuery(displayName);

              // Also set the location type for consistency
              if (bestMatch.isAirport) {
                setSelectedLocationType('airport');
              } else if (bestMatch.isPOI) {
                setSelectedLocationType('poi');
              } else if (bestMatch.isRailway) {
                setSelectedLocationType('railway');
              } else if (bestMatch.isCity) {
                setSelectedLocationType('city');
              } else {
                setSelectedLocationType('location');
              }

              searchData = {
                ...searchForm,
                latitude: parseFloat(bestMatch.latitude),
                longitude: parseFloat(bestMatch.longitude),
                adults: parseInt(searchForm.adults),
                children: parseInt(searchForm.children),
                radius: parseInt(searchForm.radius),
                page: pageOverride !== null ? parseInt(pageOverride) : parseInt(searchForm.page),
                itemsPerPage: parseInt(searchForm.itemsPerPage),
                category
              };

              data = await hotelService.searchHotelsByCoordinates(searchData);
            } else {
              throw new Error('No matching locations found');
            }
          } catch (autocompleteError) {
            console.error('Autocomplete fallback failed:', autocompleteError);
            throw new Error(`Could not find location "${locationQuery}". Please try a different location or select from the dropdown suggestions.`);
          }
        } else {
          throw new Error('Please enter a location or select from the dropdown suggestions.');
        }
      }



      // Extract hotels from the response
      const hotelList = data?.GetHotelAvailRS?.HotelAvailInfos?.HotelAvailInfo || [];
      setHotels(hotelList);
      setHasSearched(true);

      // Try to extract total count from various possible locations in the response
      let totalCount = 0;

      // First, try to get MaxSearchResults from the API response
      if (data?.GetHotelAvailRS?.HotelAvailInfos?.MaxSearchResults) {
        totalCount = parseInt(data.GetHotelAvailRS.HotelAvailInfos.MaxSearchResults);

      }

      // If not found, try status description
      if (totalCount === 0 && data?.GetHotelAvailRS?.ApplicationResults?.Status?.Description) {
        const statusDesc = data.GetHotelAvailRS.ApplicationResults.Status.Description;
        const match = statusDesc.match(/(\d+)\s+hotels?/i);
        if (match) {
          totalCount = parseInt(match[1]);

        }
      }

      // If still not found, use a more conservative fallback approach
      if (totalCount === 0) {
        if (searchType === 'hotel') {
          // For hotel-specific search, total count is just the number of results
          totalCount = hotelList.length;
        } else {
          // For location search, use pagination logic
          if (hotelList.length === searchData.itemsPerPage) {
            // Only estimate minimally - just enough to show "next" button
            totalCount = searchData.page * searchData.itemsPerPage + 1;

          } else if (hotelList.length > 0) {
            // We have some results but not a full page - this is likely the last page
            totalCount = (searchData.page - 1) * searchData.itemsPerPage + hotelList.length;

          } else {
            // No results at all
            totalCount = 0;

          }
        }
      }

      // Set pagination info with improved backend response
      if (data.pagination) {

        setPagination({
          currentPage: data.pagination.currentPage,
          itemsPerPage: data.pagination.itemsPerPage,
          totalItems: data.pagination.totalItems,
          hasMorePages: data.pagination.hasMorePages || false
        });

      } else {
        // Fallback to conservative estimation
        if (searchType === 'hotel') {
          // For hotel search, no pagination needed
          setPagination({
            currentPage: 1,
            itemsPerPage: hotelList.length,
            totalItems: hotelList.length,
            hasMorePages: false
          });
        } else {
          // For location search, use pagination logic
          const hasMorePages = hotelList.length === searchData.itemsPerPage;
          const conservativeTotalCount = hasMorePages ?
            searchData.page * searchData.itemsPerPage + 1 :
            (searchData.page - 1) * searchData.itemsPerPage + hotelList.length;

          setPagination({
            currentPage: searchData.page,
            itemsPerPage: searchData.itemsPerPage,
            totalItems: conservativeTotalCount,
            hasMorePages: hasMorePages
          });
        }
      }

    } catch (error) {
      console.error('Error searching hotels:', error);

      // More specific error messages
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        setError('Unable to connect to server. Please check if the backend is running on port 5000.');
      } else if (error.response?.status === 404) {
        setError('Hotel search endpoint not found. Please check backend configuration.');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(`Failed to search hotels: ${error.message}`);
      }

      // Reset hotels and pagination on error
      setHotels([]);
      setPagination(prev => ({
        ...prev,
        totalItems: 0,
        hasMorePages: false
      }));
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setError('');
  //   setHotels([]); // Clear previous results immediately

  //   if (!validateForm()) {
  //     return;
  //   }

  //   // Immediately switch to compact mode and scroll without waiting for API
  //   setHasSearched(true);

  //   // Reset pagination to page 1 for new searches
  //   setSearchForm(prev => ({
  //     ...prev,
  //     page: 1
  //   }));

  //   setPagination(prev => ({
  //     ...prev,
  //     currentPage: 1
  //   }));

  //   // Kick off the search without awaiting to allow UI animation
  //   searchForHotels();

  //   // Smooth scroll to results section immediately
  //   setTimeout(() => {
  //     const resultsSection = document.getElementById('results');
  //     if (resultsSection) {
  //       resultsSection.scrollIntoView({ behavior: 'smooth' });
  //     }
  //   }, 50);
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setHotels([]);

    // AGGRESSIVE DROPDOWN CLOSURE
    setShowSuggestions(false);
    setShowHotelSuggestions(false);

    // Set disabled flag
    setDropdownsDisabled(true);
    dropdownsDisabledRef.current = true;

    // Clear any pending timeouts that might reopen dropdowns
    if (window.locationSearchTimeout) {
      clearTimeout(window.locationSearchTimeout);
    }
    if (window.hotelSearchTimeout) {
      clearTimeout(window.hotelSearchTimeout);
    }

    if (!validateForm()) {
      // Re-enable dropdowns if validation fails
      setDropdownsDisabled(false);
      dropdownsDisabledRef.current = false;
      return;
    }

    setHasSearched(true);
    setSearchForm(prev => ({ ...prev, page: 1 }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    console.log('Before search hotels');
    await searchForHotels();

    // Re-enable dropdowns after search completes
    setDropdownsDisabled(false);
    dropdownsDisabledRef.current = false;

    setTimeout(() => {
      const resultsSection = document.getElementById('results');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  };



  // Utility function to safely extract string values from potentially nested objects
  const safeString = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.value) return value.value;
    return String(value) || fallback;
  };

  const formatPrice = (amount, currency = 'USD') => {
    if (!amount) return 'Price not available';

    // Handle object values
    const amountValue = safeString(amount, '0');

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(parseFloat(amountValue));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRatingStars = (rating) => {
    if (!rating) return null;

    // Handle object values
    const ratingValue = safeString(rating, '0');

    const stars = '★'.repeat(parseInt(ratingValue)) + '☆'.repeat(5 - parseInt(ratingValue));
    return stars;
  };

  const handlePageChange = (newPage) => {


    // Validate page number
    if (newPage < 1) {
      console.error('Invalid page number:', newPage);
      return;
    }

    const maxPages = Math.ceil(pagination.totalItems / pagination.itemsPerPage);
    if (newPage > maxPages) {
      console.error('Page number exceeds maximum pages:', newPage, 'max:', maxPages);
      return;
    }



    // Update form state
    setSearchForm(prev => {
      return { ...prev, page: newPage };
    });

    // Update pagination state immediately for better UX
    setPagination(prev => {
      return { ...prev, currentPage: newPage };
    });

    // Scroll to top of results when page changes (slowly)
    setTimeout(() => {
      const resultsSection = document.getElementById('results');
      const targetY = resultsSection ? resultsSection.offsetTop : 0;

      // Custom slow scroll function
      const slowScrollTo = (targetY, duration = 1500) => {
        const startY = window.pageYOffset;
        const distance = targetY - startY;
        const startTime = new Date().getTime();

        const easeInOutCubic = (t) => {
          return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        };

        const timer = setInterval(() => {
          const timeElapsed = new Date().getTime() - startTime;
          const progress = Math.min(timeElapsed / duration, 1);
          const ease = easeInOutCubic(progress);

          window.scrollTo(0, startY + distance * ease);

          if (progress === 1) {
            clearInterval(timer);
          }
        }, 16); // ~60fps
      };

      slowScrollTo(targetY);
    }, 100);

    // Pass the page directly to avoid state timing issues
    searchForHotels(newPage);
  };

  const handleResultCategoryChange = (filters) => {
    const category = filters?.all
      ? 'all'
      : ['recommended', 'compass'].filter((key) => filters?.[key]).join(',') || 'all';
    setSearchForm((prev) => ({ ...prev, page: 1, category }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    searchForHotels(1, category);
  };

  // Function to check if we should allow navigation to a specific page
  const canNavigateToPage = (pageNumber) => {
    // Always allow navigation to page 1
    if (pageNumber === 1) return true;

    // If we have no hotels at all, don't allow navigation beyond page 1
    if (hotels.length === 0) return false;

    // Use hasMorePages from backend for more accurate navigation
    if (pageNumber === pagination.currentPage + 1) {
      return pagination.hasMorePages;
    }

    // For previous pages, allow navigation if within current page bounds
    if (pageNumber < pagination.currentPage) {
      return true;
    }

    // For calculated max pages (when we have reliable total count)
    const maxPages = Math.ceil(pagination.totalItems / pagination.itemsPerPage);
    return pageNumber <= maxPages;
  };

  // Helper function to determine if pagination should be shown
  const shouldShowPagination = () => {
    // Show pagination if we have hotels and either:
    // 1. We have more pages available (from backend), OR
    // 2. We're not on page 1 (indicating we've navigated), OR
    // 3. We have more items than fit on one page
    const hasMorePagesAvailable = pagination.hasMorePages;
    const notOnFirstPage = pagination.currentPage > 1;
    const hasMultiplePages = pagination.totalItems > pagination.itemsPerPage;



    return hotels.length > 0 && (hasMorePagesAvailable || notOnFirstPage || hasMultiplePages);
  };

  // Area search function for when hotel is not available
  const handleAreaSearch = async () => {
    try {
      setLoading(true);
      setError('');
      setHotels([]); // Clear previous results immediately

      // Switch to location search mode
      setSearchType('location');
      setSelectedHotelCode('');
      setHotelQuery('');

      // Check if we have coordinates from the hotel search
      if (searchForm.latitude && searchForm.longitude) {
        setLocationQuery('Searching nearby hotels...');

        // Perform area search using the hotel's coordinates
        const areaSearchData = {
          ...searchForm,
          latitude: parseFloat(searchForm.latitude),
          longitude: parseFloat(searchForm.longitude),
          adults: parseInt(searchForm.adults),
          children: parseInt(searchForm.children),
          childAges: searchForm.childAges,
          radius: parseInt(searchForm.radius),
          page: 1,
          itemsPerPage: parseInt(searchForm.itemsPerPage),
          category: searchForm.category || 'all'
        };

        const data = await hotelService.searchHotelsByCoordinates(areaSearchData);
        const hotelList = data?.GetHotelAvailRS?.HotelAvailInfos?.HotelAvailInfo || [];

        setHotels(hotelList);

        // Try to get a better location name from the first hotel result
        if (hotelList.length > 0 && hotelList[0].HotelInfo?.LocationInfo?.Address) {
          const firstHotel = hotelList[0];
          const city = firstHotel.HotelInfo.LocationInfo.Address.CityName;
          const country = firstHotel.HotelInfo.LocationInfo.Address.CountryName;
          if (city && country) {
            setLocationQuery(`${city}, ${country}`);
          } else if (city) {
            setLocationQuery(city);
          } else {
            setLocationQuery('this area');
          }
        } else {
          setLocationQuery('this area');
        }

        // Set pagination
        if (data.pagination) {
          setPagination(data.pagination);
        } else {
          setPagination({
            currentPage: 1,
            itemsPerPage: areaSearchData.itemsPerPage,
            totalItems: hotelList.length,
            hasMorePages: false
          });
        }

        // Scroll to results
        setTimeout(() => {
          const resultsSection = document.getElementById('results');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);

      } else {
        // No coordinates available, just reset to search mode
        setLocationQuery('');
        setHasSearched(false);
      }
    } catch (error) {
      console.error('Error searching nearby hotels:', error);
      setError('Failed to search nearby hotels. Please try again.');
      setLocationQuery('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen relative pt-0 overflow-x-hidden" style={{ backgroundColor: '#f7f7f7' }}>

      {/* Search Section */}
      <section className="relative z-50">
        <div className={hasSearched ? "sticky top-[7.25rem] left-0 right-0 z-50 max-w-full" : ""} style={hasSearched ? { zIndex: 9998 } : {}}>
          <SearchForm
            searchForm={searchForm}
            locationQuery={locationQuery}
            hotelQuery={hotelQuery}
            locationSuggestions={locationSuggestions}
            hotelSuggestions={hotelSuggestions}
            showSuggestions={showSuggestions}
            showHotelSuggestions={showHotelSuggestions}
            loading={loading}
            error={error}
            onLocationChange={handleLocationChange}
            onHotelNameChange={handleHotelNameChange}
            onInputChange={handleInputChange}
            onSelectLocation={selectLocation}
            onSelectHotel={selectHotel}
            onSubmit={handleSubmit}
            safeString={safeString}
            compact={hasSearched}
          />
        </div>
      </section>

      <div className="overflow-x-hidden">
        {!hasSearched && (
          <div id="hotel-catalog" className="relative z-10">
            <HotelCatalog
              hotels={catalogHotels}
              loading={catalogLoading}
            />
          </div>
        )}

        {/* Hotel List Section */}
        {hasSearched && (
          <section id="results" className="relative z-10 py-6 sm:py-10 lg:py-12" style={{ backgroundColor: '#f7f7f7' }}>
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full min-w-0">

              <HotelSearchResults
                hasSearched={hasSearched}
                hotels={hotels}
                loading={loading}
                locationQuery={displayLocationQuery}
                searchForm={searchForm}
                pagination={pagination}
                error={error}
                onBackToSearch={() => {
                  setHasSearched(false);
                  setHotels([]);
                  setError('');
                  setSearchType('location');
                  setSelectedHotelCode('');
                  setHotelQuery('');
                  setLocationQuery('');
                  setDisplayLocationQuery('');
                  setDisplayHotelQuery('');
                }}
                onPageChange={handlePageChange}
                onCategoryFilterChange={handleResultCategoryChange}
                formatDate={formatDate}
                getRatingStars={getRatingStars}
                formatPrice={formatPrice}
                safeString={safeString}
                searchType={searchType}
                hotelQuery={displayHotelQuery}
                selectedHotelCode={selectedHotelCode}
                onAreaSearch={handleAreaSearch}
              />
            </div>
          </section>
        )}
      </div>

    </div>
    </MainLayout>
  );
};

export default HotelSearchPage;
